/**
 * Aufbau eines Abstiegs: Karte, Spieler, Gegner, Begleiter, Aufgabe.
 *
 * Alle Zahlen kommen aus `packages/content/data/regelwerk.json` — dieser Code
 * liest sie, er setzt sie nicht (Ueberarbeitung A4). Wer balancen will, ändert
 * das Regelwerk, nicht diese Datei.
 *
 * Alles hier ist deterministisch aus dem Seed.
 */

import { Zufall, type Strom } from '@pg/rng';
import type { Vek } from '@pg/protocol';
import { ladeInhalte, rangStufe, type Gegner, type Inhalte, type Merkmal, type Rang } from '@pg/content';
import type {
  Aufgabe,
  Automatikgrad,
  Begleiter,
  GegnerWesen,
  Gegenstand,
  Raum,
  Spieler,
  Welt,
} from './typen.js';
import { baueKarte, naechsterBoden, planFuerTiefe } from './karte.js';
import { vek } from './vektor.js';

export interface WeltOptionen {
  seed: string;
  region?: string;
  tiefe?: number;
  grad?: number;
  klasse?: string;
  /** Freigeschaltete Fähigkeiten. Ohne Angabe: nur die Startfähigkeit. */
  freigeschaltet?: string[];
  /** Belegung der Plätze. Ohne Angabe: die freigeschalteten der Reihe nach. */
  faehigkeiten?: string[];
  /** Fähigkeiten, die auf Handbetrieb stehen (feuern nie von selbst). */
  handbetrieb?: string[];
  automatikgrad?: Automatikgrad;
  /** Gangart: 'flink' oder 'gemessen'. */
  tempoprofil?: string;
  stufe?: number;
  erfahrung?: number;
  ausruestung?: Record<string, Gegenstand | null>;
  pech?: Record<string, number>;
}

export interface WeltMitZufall {
  welt: Welt;
  zufall: Zufall;
  inhalte: Inhalte;
}

/** Erfahrung, die für die nächste Stufe fehlt. */
export function erfahrungFuerStufe(stufe: number, inhalte?: Inhalte): number {
  const r = (inhalte ?? ladeInhalte()).regelwerk.erfahrung;
  return Math.round(r.basis * Math.pow(stufe, r.exponent));
}

/** Wird auf dieser Stufe eine neue Fähigkeit freigeschaltet? */
export function freischaltungAufStufe(stufe: number, inhalte: Inhalte): boolean {
  return inhalte.regelwerk.faehigkeiten.freischaltStufen.includes(stufe);
}

/**
 * Angebote für eine Freischaltung — deterministisch aus dem Strom.
 * Der Spieler wählt eines; das ist der Moment, in dem er sich mit der Figur
 * befasst (Ueberarbeitung B10).
 */
export function baueAngebote(spieler: Spieler, inhalte: Inhalte, strom: Strom): string[] {
  const r = inhalte.regelwerk.faehigkeiten;
  const offen = [...inhalte.faehigkeiten.values()]
    .filter((f) => f.klasse === spieler.klasse && !spieler.freigeschaltet.includes(f.id))
    .map((f) => f.id)
    .sort();
  if (offen.length === 0) return [];
  return strom.mische(offen).slice(0, Math.min(r.angeboteJeFreischaltung, offen.length));
}

/** Leitet die Spielerwerte aus Grundwerten und Ausrüstung ab. */
export function berechneWerte(spieler: Spieler, inhalte?: Inhalte): void {
  const i = inhalte ?? ladeInhalte();
  const r = i.regelwerk;
  const g = {
    leben: r.spieler.leben.basis + (spieler.stufe - 1) * r.spieler.leben.jeStufe,
    vorrat: r.spieler.vorrat.basis + (spieler.stufe - 1) * r.spieler.vorrat.jeStufe,
    schaden: r.spieler.schaden.basis + (spieler.stufe - 1) * r.spieler.schaden.jeStufe,
    tempo: r.spieler.tempo,
  };

  let leben = g.leben;
  let vorrat = g.vorrat;
  let schaden = g.schaden;
  let tempoAnteil = 0;
  let ruestung = 0;
  let widerstand = 0;
  let kritchance = r.spieler.kritchanceBasis;
  let kritschaden = r.spieler.kritschadenBasis;
  let lebensentzug = 0;
  let spuersinn = 0;
  let schadenAnteil = 0;

  for (const slot of ['waffe', 'ruestung', 'umhang', 'ring']) {
    const stueck = spieler.ausruestung[slot];
    if (!stueck) continue;

    for (const [name, wert] of Object.entries(stueck.grundwerte)) {
      if (wert === undefined) continue;
      if (name === 'schaden') schaden += wert;
      else if (name === 'leben') leben += wert;
      else if (name === 'ruestung') ruestung += wert;
      else if (name === 'tempo') tempoAnteil += wert;
    }

    for (const a of stueck.affixe) {
      const flach = a.art === 'flach';
      switch (a.wert) {
        case 'schaden': flach ? (schaden += a.betrag) : (schadenAnteil += a.betrag); break;
        case 'leben': leben += a.betrag; break;
        case 'ruestung': ruestung += a.betrag; break;
        case 'tempo': tempoAnteil += a.betrag; break;
        case 'vorrat': vorrat += a.betrag; break;
        case 'widerstand-glut': widerstand += a.betrag; break;
        case 'spuersinn': spuersinn += a.betrag; break;
        case 'kritchance': kritchance += a.betrag; break;
        case 'kritschaden': kritschaden += a.betrag; break;
        case 'lebensentzug': lebensentzug += a.betrag; break;
      }
    }
  }

  const anteilVoll = spieler.lebenMax > 0 ? spieler.leben / spieler.lebenMax : 1;

  spieler.lebenMax = Math.round(leben);
  spieler.leben = Math.max(1, Math.round(spieler.lebenMax * anteilVoll));
  spieler.vorratMax = Math.round(vorrat);
  spieler.vorrat = Math.min(spieler.vorrat, spieler.vorratMax);
  spieler.schaden = schaden * (1 + schadenAnteil);
  spieler.tempo = g.tempo * (1 + tempoAnteil);
  spieler.ruestung = ruestung;
  spieler.widerstandGlut = Math.min(r.kampf.widerstandsdeckel, widerstand);
  spieler.kritchance = Math.min(0.75, kritchance);
  spieler.kritschaden = kritschaden;
  spieler.lebensentzug = lebensentzug;
  spieler.spuersinn = spuersinn;
}

/** Was die Zunft jedem Absteiger mitgibt. Werkzeug, keine Beute. */
function standardAusruestung(inhalte: Inhalte): Record<string, Gegenstand | null> {
  const mache = (basisId: string): Gegenstand | null => {
    const b = inhalte.basen.find((x) => x.id === basisId);
    if (!b) return null;
    return {
      id: 0,
      basis: b.id,
      name: b.name,
      slot: b.slot,
      seltenheit: 'schlicht',
      fundtiefe: 1,
      praegelevel: 1,
      affixe: [],
      grundwerte: { ...b.grundwerte },
      fassungen: 0,
      bindung: b.bindung,
      bindungMax: b.bindung,
    };
  };
  return {
    waffe: mache('zunfthammer'),
    ruestung: mache('arbeitskittel'),
    umhang: null,
    ring: null,
  };
}

function baueSpieler(o: WeltOptionen, inhalte: Inhalte, start: Vek): Spieler {
  const r = inhalte.regelwerk;
  const stufe = o.stufe ?? 1;
  const freigeschaltet = o.freigeschaltet ?? [r.faehigkeiten.startFaehigkeit];
  const belegung = (o.faehigkeiten ?? freigeschaltet).slice(0, r.faehigkeiten.plaetze);
  const hand = new Set(o.handbetrieb ?? []);

  const spieler: Spieler = {
    id: 1,
    bauplan: o.klasse ?? 'kesselschmied',
    klasse: o.klasse ?? 'kesselschmied',
    pos: vek(start.x, start.y),
    gesch: vek(),
    winkel: 0,
    radius: r.spieler.radius,
    leben: 1,
    lebenMax: 1,
    tempo: r.spieler.tempo,
    schaden: r.spieler.schaden.basis,
    tot: false,
    totSeit: 0,
    bereitIn: 0,
    telegraf: 0,
    wirktNoch: 0,
    wirktGesamt: 0,
    stoss: vek(),
    brennt: null,
    betaeubtBis: 0,
    stufe,
    erfahrung: o.erfahrung ?? 0,
    vorrat: r.spieler.vorrat.basis,
    vorratMax: r.spieler.vorrat.basis,
    vorratRegen: r.spieler.vorratRegen,
    lebenRegen: r.spieler.lebenRegenAnteil,
    ohneSchadenSeit: 99,
    gold: 0,
    ruestung: 0,
    widerstandGlut: 0,
    kritchance: r.spieler.kritchanceBasis,
    kritschaden: r.spieler.kritschadenBasis,
    lebensentzug: 0,
    spuersinn: 0,
    slots: belegung.map((id) => {
      const f = inhalte.faehigkeiten.get(id);
      if (!f) throw new Error(`Unbekannte Faehigkeit: ${id}`);
      return {
        faehigkeit: f,
        handbetrieb: hand.has(id),
        abklingen: 0,
        manuellAngefordert: false,
        manuellZiel: null,
        regelAktiv: false,
      };
    }),
    staerkungen: [],
    autolauf: false,
    anker: false,
    richtung: vek(0, 0),
    steuertGerade: false,
    automatikgrad: o.automatikgrad ?? (r.faehigkeiten.standardAutomatikgrad as Automatikgrad),
    freigeschaltet: [...freigeschaltet],
    offeneWahl: null,
    ausruestung: o.ausruestung ?? standardAusruestung(inhalte),
  };

  berechneWerte(spieler, inhalte);
  spieler.leben = spieler.lebenMax;
  spieler.vorrat = spieler.vorratMax;
  return spieler;
}

function baueBegleiter(start: Vek): Begleiter {
  return {
    id: 2,
    pos: vek(start.x - 1, start.y),
    gesch: vek(),
    winkel: 0,
    radius: 0.26,
    korb: [],
    korbMax: 12,
    zustand: 'folgt',
    zielBeute: null,
    zurueckIn: 0,
  };
}

/** Skalierung nach Tiefe und Grad. Kanon §12.6, Werte aus dem Regelwerk. */
export function skalierung(
  tiefe: number,
  grad: number,
  inhalte?: Inhalte,
): { leben: number; schaden: number } {
  const g = (inhalte ?? ladeInhalte()).regelwerk.gegner;
  return {
    leben: (1 + (tiefe - 1) * g.tiefeLebenJeStufe) * Math.pow(g.gradLeben, grad - 1),
    schaden: (1 + (tiefe - 1) * g.tiefeSchadenJeStufe) * Math.pow(g.gradSchaden, grad - 1),
  };
}

/**
 * Baut einen Gegner mit Rang und Merkmalen.
 *
 * Merkmale sind Daten, keine Sonderfälle: Sie verändern hier die Grundwerte,
 * und was zur Laufzeit wirkt, wertet das System `merkmale` aus. Eine neue
 * Gegner-Eigenart ist damit ein Eintrag im Regelwerk, kein Eingriff im Code.
 */
export function baueGegner(
  welt: Welt,
  inhalte: Inhalte,
  def: Gegner,
  x: number,
  y: number,
  rang: Rang,
  strom: Strom,
  abspaltung = false,
): GegnerWesen {
  const r = inhalte.regelwerk;
  const rd = r.gegner.raenge[rang];
  const s = skalierung(welt.tiefe, welt.grad, inhalte);

  const merkmale: Merkmal[] = [];
  if (rd.merkmale > 0) {
    const gemischt = strom.mische(r.gegner.merkmale);
    for (let i = 0; i < Math.min(rd.merkmale, gemischt.length); i++) {
      merkmale.push(gemischt[i] as Merkmal);
    }
  }

  // Der Grundfaktor gilt fuer ALLE Raenge — sonst stehen die Verhaeltnisse
  // zwischen Trash und Anfuehrer schief.
  let leben = def.leben * s.leben * r.gegner.gewoehnlichLebenFaktor * rd.leben;
  let tempo = def.tempo;
  for (const m of merkmale) {
    if (m.art === 'leben' && m.faktor) leben *= m.faktor;
    if (m.art === 'tempo' && m.faktor) tempo *= m.faktor;
  }
  if (abspaltung) leben *= 0.35;

  const zusatz = rd.name ? `, ${rd.name}` : '';
  const merkmalText = merkmale.length > 0 ? ` (${merkmale.map((m) => m.name).join(', ')})` : '';

  return {
    id: welt.naechsteId++,
    bauplan: def.id,
    def,
    rang,
    merkmale,
    anzeigename: `${def.name}${zusatz}${merkmalText}`,
    auraTakt: 0,
    abspaltung,
    pos: vek(x, y),
    gesch: vek(),
    winkel: strom.spanne(0, Math.PI * 2),
    radius: def.radius * rd.radius,
    leben: Math.round(leben),
    lebenMax: Math.round(leben),
    tempo,
    schaden: def.schaden * s.schaden * rd.schaden,
    tot: false,
    totSeit: 0,
    bereitIn: strom.spanne(0, def.angriffstakt),
    telegraf: 0,
    wirktNoch: 0,
    wirktGesamt: 0,
    stoss: vek(),
    brennt: null,
    betaeubtBis: 0,
    zielId: 1,
    umlauf: strom.chance(0.5) ? 1 : -1,
    angekuendigt: false,
    wach: false,
  };
}

/**
 * Gegner werden pro Raum gesetzt, nicht gleichmäßig über die Karte — so
 * entstehen Gruppen und ruhige Wege dazwischen.
 *
 * Nach dem Spieltest (Ueberarbeitung B6/B7): weniger, dafür spürbarere Gegner,
 * und **mindestens ein Vorarbeiter je Abstieg** ab Tiefe 1. Ohne Ankerpunkt
 * fühlt sich jede Gruppe wie Trash an.
 */
function setzeGegner(welt: Welt, inhalte: Inhalte, zufall: Zufall): void {
  const r = inhalte.regelwerk;
  const strom = zufall.strom('spawn');
  // Vielfalt nach Tiefe: Der erste Abstieg zeigt zwei Arten, nicht sechs.
  const passend = inhalte.gegnerListe.filter((g) => (g.abTiefe ?? 1) <= welt.tiefe);
  // Gewoehnliche Spawns: alles Unbenannte, gewichtet — schwere Einheiten
  // kommen vor, aber selten.
  const gewoehnlich = passend.filter((g) => !g.benannt);
  const gewoehnlichGewichte = gewoehnlich.map((g) => g.spawngewicht ?? 40);
  // Anfuehrer NUR aus gewoehnlichen Bauplaenen. Ein Champion aus einem
  // schweren Bauplan war 46 Sekunden Arbeit — weit ueber dem Zielrhythmus.
  const anfuehrerArten = passend.filter((g) => !g.benannt && !g.elite);
  const anfuehrerGewichte = anfuehrerArten.map((g) => g.spawngewicht ?? 40);
  const ausklang = inhalte.gegnerListe.find((g) => g.benannt);

  const kammern = welt.karte.raeume.filter((x) => x.art === 'kammer' || x.art === 'wahrzeichen');

  // Anzahl der Anfuehrer waechst mit der Tiefe, nicht nur mit der Kammerzahl.
  // Gemessen standen auf Tiefe 3 bereits drei Champions — mit drei Faehigkeiten
  // ueberlebte dort nur einer von sechs Laeufen.
  const vorarbeiterZiel =
    welt.tiefe < r.gegner.vorarbeiterAbTiefe
      ? 0
      : Math.min(
          1 + Math.floor(welt.tiefe / 4),
          Math.max(
            r.gegner.vorarbeiterJeAbstieg.mindestens,
            Math.round(kammern.length * r.gegner.vorarbeiterJeAbstieg.jeKammernRest),
          ),
        );
  const gezeichneteZiel =
    welt.tiefe >= r.gegner.gezeichneterAbTiefe
      ? Math.min(
          r.gegner.gezeichneterJeAbstieg.hoechstens,
          Math.max(r.gegner.gezeichneterJeAbstieg.mindestens, Math.floor(welt.tiefe / 6)),
        )
      : 0;

  const reihenfolge = strom.mische(kammern);
  const mitVorarbeiter = new Set(reihenfolge.slice(0, vorarbeiterZiel));
  const mitGezeichnetem = new Set(
    reihenfolge.slice(vorarbeiterZiel, vorarbeiterZiel + gezeichneteZiel),
  );

  const flaecheJeGegner = Math.max(
    r.gegner.dichteFlaecheJeGegner.mindestens,
    r.gegner.dichteFlaecheJeGegner.basis + welt.tiefe * r.gegner.dichteFlaecheJeGegner.jeTiefe,
  );

  const setzeIn = (raum: Raum, def: Gegner, rang: Rang): GegnerWesen => {
    const p = naechsterBoden(
      welt.karte,
      raum.x + strom.ganz(2, Math.max(2, raum.b - 3)),
      raum.y + strom.ganz(2, Math.max(2, raum.h - 3)),
    );
    const wesen = baueGegner(welt, inhalte, def, p.x + 0.5, p.y + 0.5, rang, strom);
    welt.gegner.push(wesen);
    return wesen;
  };

  for (const raum of welt.karte.raeume) {
    if (raum.art === 'start') continue;

    if (raum.art === 'ausklang') {
      if (ausklang) {
        welt.gegner.push(
          baueGegner(
            welt,
            inhalte,
            ausklang,
            welt.karte.ziel.x,
            welt.karte.ziel.y,
            'ausklang',
            strom,
          ),
        );
      }
      for (let i = 0; i < 4; i++) setzeIn(raum, strom.gewichtet(gewoehnlich, gewoehnlichGewichte), 'gewoehnlich');
      continue;
    }

    const anzahl = Math.max(2, Math.round((raum.b * raum.h) / flaecheJeGegner) + strom.ganz(-1, 1));
    for (let i = 0; i < anzahl; i++) setzeIn(raum, strom.gewichtet(gewoehnlich, gewoehnlichGewichte), 'gewoehnlich');

    // Anführer plus Gefolge — das macht aus einer Ansammlung eine Gruppe.
    if (mitVorarbeiter.has(raum)) {
      const def = strom.gewichtet(anfuehrerArten, anfuehrerGewichte);
      const anfuehrer = setzeIn(raum, def, 'vorarbeiter');
      const gefolge = r.gegner.raenge.vorarbeiter.gefolge ?? [2, 3];
      const wieViele = strom.ganz(gefolge[0], gefolge[1]);
      for (let i = 0; i < wieViele; i++) {
        const p = naechsterBoden(
          welt.karte,
          Math.round(anfuehrer.pos.x + strom.spanne(-3, 3)),
          Math.round(anfuehrer.pos.y + strom.spanne(-3, 3)),
        );
        welt.gegner.push(
          baueGegner(welt, inhalte, def, p.x + 0.5, p.y + 0.5, 'gewoehnlich', strom),
        );
      }
    }

    if (mitGezeichnetem.has(raum)) {
      setzeIn(raum, strom.gewichtet(anfuehrerArten, anfuehrerGewichte), 'gezeichneter');
    }
  }
}

function baueAufgabe(inhalte: Inhalte): Aufgabe {
  // Erlaubte Formen: Game-Design §3.4. NIE "sammle N gewoehnliche Dinge".
  const ausklang = inhalte.gegnerListe.find((g) => g.benannt);
  return {
    art: 'benanntes-ziel',
    text: ausklang
      ? `Finde und erschlage ${ausklang.akkusativ ?? ausklang.name}.`
      : 'Erreiche die tiefste Kammer.',
    fortschritt: 0,
    ziel: 1,
  };
}

export function erstelleWelt(o: WeltOptionen): WeltMitZufall {
  const inhalte = ladeInhalte();
  const zufall = new Zufall(o.seed);
  const tiefe = o.tiefe ?? 1;
  const grad = o.grad ?? 1;

  const karte = baueKarte(
    zufall.strom('welt'),
    planFuerTiefe(tiefe, inhalte.regelwerk),
    inhalte.regelwerk,
  );
  const spieler = baueSpieler(o, inhalte, karte.start);

  const welt: Welt = {
    takt: 0,
    seed: o.seed,
    region: o.region ?? 'schmelzgaenge',
    tiefe,
    grad,
    karte,
    spieler,
    begleiter: baueBegleiter(karte.start),
    gegner: [],
    geschosse: [],
    beute: [],
    aufgabe: baueAufgabe(inhalte),
    stand: 'laeuft',
    naechsteId: 10,
    ertrag: { gold: 0, stuecke: 0 },
    pech: o.pech ?? {},
    nachschub: [],
    tempoprofil: o.tempoprofil ?? inhalte.regelwerk.tempoProfile.standard,
    aufhebeReichweite: 2.2,
    ausgang: null,
    einsteigen: false,
  };

  setzeGegner(welt, inhalte, zufall);
  return { welt, zufall, inhalte };
}

/**
 * Welche Fähigkeiten ein Charakter dieser Stufe plausibel hätte.
 *
 * Gebraucht von Tests und vom Client, wenn ein Lauf auf einer höheren Stufe
 * beginnt (Balance-Messungen, spätere Fortsetzung). Im echten Spiel wählt der
 * Spieler — das hier ist die neutrale Annahme, nicht die Regel.
 */
export function standardFreischaltungen(
  stufe: number,
  klasse: string,
  inhalte: Inhalte,
): string[] {
  const r = inhalte.regelwerk.faehigkeiten;
  const passend = [...inhalte.faehigkeiten.values()]
    .filter((f) => f.klasse === klasse && (f.abStufe ?? 1) <= stufe)
    .sort((a, b) => (a.abStufe ?? 1) - (b.abStufe ?? 1) || a.id.localeCompare(b.id))
    .map((f) => f.id);

  const aus = [r.startFaehigkeit, ...passend.filter((id) => id !== r.startFaehigkeit)];
  // Höchstens so viele, wie es Freischaltungen bis zu dieser Stufe gab.
  const freigeschaltet = 1 + r.freischaltStufen.filter((s) => s <= stufe).length;
  return aus.slice(0, Math.min(r.plaetze, freigeschaltet));
}

/** Höchster Rang unter den übergebenen Gegnern — für die Regelauswertung. */
export function hoechsterRang(gegner: readonly GegnerWesen[]): Rang {
  let beste: Rang = 'gewoehnlich';
  for (const g of gegner) {
    if (g.tot) continue;
    if (rangStufe(g.rang) > rangStufe(beste)) beste = g.rang;
  }
  return beste;
}
